import { ChatBubbleIcon, ExternalLinkIcon } from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  MeetingAdminResponse,
  MeetingLimitedResponse,
  MeetingsService,
  ParentLimitedResponse,
  StudentLimitedResponse,
  StudentsService,
  TutorLimitedResponse,
} from "client/openapi";
import { AttentionIssue } from "client/openapi/models/AttentionIssue";
import { PersonKind } from "client/openapi/models/PersonKind";
import type { NextMeeting } from "client/openapi/models/NextMeeting";
import type { PersonName } from "client/openapi/models/PersonName";
import type { PersonRow } from "client/openapi/models/PersonRow";
import Avatar from "components/Avatar";
import ComposeMessageDialog from "components/Dashboard/ComposeMessageDialog";
import PersonPill, { PillColor } from "components/PersonPill";
import { Dialog, DialogTrigger } from "components/Dialog";
import MeetingDialog from "components/MeetingDialog";
import moment from "moment";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import Notifications from "util/notifications";
import { generateInitials } from "util/generateInitials";
import { StudentCreditsDialog } from "pages/manage/students/[id]/PaymentsTab/Credit";
import TutoringUpfrontModal from "pages/manage/students/[id]/PaymentsTab/Modals/Charges/TutoringUpfront";
import CreditMenu from "./CreditMenu";
import DismissButton from "./DismissButton";
import IconTip from "./IconTip";
import LinkFamilyDialog from "./LinkFamilyDialog";
import MeetingRequests from "./MeetingRequests";
import OnboardingSteps from "./OnboardingSteps";
import RequestPaymentMethodDialog from "./RequestPaymentMethodDialog";
import { TaskChips } from "./TasksDue";
import { KindChip, personDetail, personName, profilePath } from "./person";
import QuickAddTask from "./QuickAddTask";
import { safePhoto } from "util/safePhoto";

const dollars = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

/** "Today · 3:30 pm" or "Mon, Oct 5 · 4:00 pm", in local time like the calendar. */
function formatMeeting(at: string): string {
  const start = moment(at);
  const day = start.isSame(moment(), "day")
    ? "Today"
    : start.format("ddd, MMM D");
  return `${day} · ${start.format("h:mm a")}`;
}

// Issues with chips of their own (the requests and the tasks), not issue chips.
const OWN_CHIPS = [AttentionIssue.MEETING_REQUEST, AttentionIssue.TASK_DUE];

/**
 * Each issue's name, as its chip says it, in Needs attention's order: the
 * "Show people with:" pills.
 */
export const ISSUE_LABELS: Record<AttentionIssue, string> = {
  [AttentionIssue.MEETING_REQUEST]: "Meeting request",
  [AttentionIssue.TASK_DUE]: "Task due",
  [AttentionIssue.CHARGE_FAILED]: "Charge failed",
  [AttentionIssue.OWES]: "Owes",
  [AttentionIssue.NO_PAYMENT_METHOD]: "No card or bank",
  [AttentionIssue.NO_PAYROLL_BANK]: "No payroll bank",
};

function issueLabel(row: PersonRow, issue: AttentionIssue): string {
  if (issue === AttentionIssue.OWES) {
    return `Owes ${dollars.format(row.owed_cents / 100)}`;
  }
  if (issue === AttentionIssue.CHARGE_FAILED && row.failed_charges !== 1) {
    return `${row.failed_charges} charges failed`;
  }
  return ISSUE_LABELS[issue];
}

/** The Communication tab's colors. */
const FAMILY_COLORS: Record<PersonKind, PillColor> = {
  [PersonKind.STUDENT]: "green",
  [PersonKind.PARENT]: "blue",
  [PersonKind.TUTOR]: "gold",
};

const NO_FAMILY: Record<PersonKind, string> = {
  [PersonKind.STUDENT]: "No parent linked",
  [PersonKind.PARENT]: "No student linked",
  [PersonKind.TUTOR]: "No students assigned",
};

/**
 * The next meeting as a green pill. It opens the meeting's dialog, the one
 * the calendar opens, after loading the meeting.
 */
function NextMeetingChip({
  meeting,
  onMeetingChange,
}: {
  meeting: NextMeeting;
  onMeetingChange: () => void;
}) {
  const [event, setEvent] = useState<
    MeetingAdminResponse | MeetingLimitedResponse
  >();
  const [loading, setLoading] = useState(false);
  const label = [formatMeeting(meeting.start), meeting.name]
    .filter(Boolean)
    .join(" · ");

  function open() {
    setLoading(true);
    MeetingsService.getMeetingById({ meetingId: meeting.id })
      .then(setEvent)
      .catch(() => Notifications.error("Could not open the meeting."))
      .finally(() => setLoading(false));
  }

  return (
    <Dialog
      open={!!event}
      onOpenChange={(isOpen) => !isOpen && setEvent(undefined)}
    >
      <button
        type="button"
        className="people-panel__meeting"
        title={label}
        disabled={loading}
        onClick={open}
      >
        <PersonPill label={label} color="green" />
      </button>
      {event && (
        <MeetingDialog
          role={Auth0AccountRole.ORG_ADMIN}
          event={event}
          setEvents={() => {
            onMeetingChange();
            return Promise.resolve();
          }}
        />
      )}
    </Dialog>
  );
}

/**
 * A person's issues other than requests and tasks, as chips: No card or bank
 * asks for one, a balance opens a menu to view or charge the student's
 * credits, the rest open the person's page. The People panel's rows and the
 * CRM board's Needs attention window show them.
 */
export function IssueChips({
  row,
  orgId,
  onChange,
}: {
  row: PersonRow;
  orgId: number;
  /** The balance may have changed in the credits or charge window. */
  onChange: () => void;
}) {
  const issueChips = row.issues.filter((issue) => !OWN_CHIPS.includes(issue));
  const [requestingMethod, setRequestingMethod] = useState(false);
  // The balance's windows live here, not in its chip, so they stay open when
  // the balance is paid off and the chip goes.
  const [showingCredits, setShowingCredits] = useState(false);
  // The charge window takes the student, loaded as the student's page loads it.
  const [charging, setCharging] = useState<StudentLimitedResponse>();
  const [loadingCharge, setLoadingCharge] = useState(false);
  // The balance may have changed in the credits window: reload the row.
  const closeCredits = useCallback(() => {
    setShowingCredits(false);
    onChange();
  }, [onChange]);

  function charge() {
    setLoadingCharge(true);
    StudentsService.getStudent({ studentId: row.id })
      .then(setCharging)
      .catch(() => Notifications.error("Could not open the charge."))
      .finally(() => setLoadingCharge(false));
  }

  return (
    <>
      {issueChips.map((issue) =>
        issue === AttentionIssue.OWES ? (
          <CreditMenu
            key={issue}
            onView={() => setShowingCredits(true)}
            onCharge={charge}
          >
            <button
              type="button"
              className="people-chip people-chip--alert"
              disabled={showingCredits || loadingCharge || !!charging}
            >
              {issueLabel(row, issue)}
            </button>
          </CreditMenu>
        ) : issue === AttentionIssue.NO_PAYMENT_METHOD ? (
          <button
            key={issue}
            type="button"
            className="people-chip people-chip--alert"
            title="Request a card or bank by text or email"
            onClick={() => setRequestingMethod(true)}
          >
            {issueLabel(row, issue)}
          </button>
        ) : (
          <Link
            key={issue}
            to={`${profilePath(row)}#${
              issue === AttentionIssue.NO_PAYROLL_BANK ? "payroll" : "billing"
            }`}
            className="people-chip people-chip--alert"
          >
            {issueLabel(row, issue)}
          </Link>
        )
      )}
      {requestingMethod && (
        <RequestPaymentMethodDialog
          row={row}
          orgId={orgId}
          onClose={() => setRequestingMethod(false)}
        />
      )}
      {showingCredits && (
        <StudentCreditsDialog
          studentId={row.id}
          orgId={orgId}
          onClose={closeCredits}
        />
      )}
      {charging && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setCharging(undefined);
              onChange();
            }
          }}
        >
          <TutoringUpfrontModal student={charging} orgId={orgId} />
        </Dialog>
      )}
    </>
  );
}

/**
 * Linked people as pills in the Communication tab's colors, each opening
 * their page in a new tab. They stay on one line; when they don't fit, the
 * cell scrolls. A student with no parent, or a parent with no student,
 * links one from here.
 */
function Family({
  row,
  orgId,
  onChange,
}: {
  row: PersonRow;
  orgId: number;
  onChange: () => void;
}) {
  const [linking, setLinking] = useState(false);
  if (row.family.length === 0) {
    return (
      <div className="people-panel__family">
        {row.kind === PersonKind.TUTOR ? (
          <PersonPill label={NO_FAMILY[row.kind]} />
        ) : (
          <button
            type="button"
            className="people-panel__link-family"
            title={`Link ${
              row.kind === PersonKind.STUDENT ? "a parent" : "a student"
            }`}
            onClick={() => setLinking(true)}
          >
            <PersonPill label={NO_FAMILY[row.kind]} />
          </button>
        )}
        {linking && (
          <LinkFamilyDialog
            row={row}
            orgId={orgId}
            onClose={() => setLinking(false)}
            onChange={onChange}
          />
        )}
      </div>
    );
  }
  return (
    <div className="people-panel__family">
      {row.family.map((member) => {
        const name = personName(member) || "No name";
        return (
          <Link
            key={`${member.kind}-${member.id}`}
            to={profilePath(member)}
            target="_blank"
            className="flex min-w-0"
          >
            <PersonPill label={name} color={FAMILY_COLORS[member.kind]} />
          </Link>
        );
      })}
    </div>
  );
}

export default function PersonTableRow({
  row,
  orgId,
  onDismiss,
  onChange,
  showOnboarding = false,
}: {
  row: PersonRow;
  orgId: number;
  onDismiss?: () => void;
  /**
   * Something on the row changed from one of its windows: a meeting, a
   * request answered, a task, a step, a link.
   */
  onChange: () => void;
  /** The Onboarding tab: the status shows the person's steps, and there is
   * no Next meeting column. */
  showOnboarding?: boolean;
}) {
  const name = personName(row);
  const detail = personDetail(row);
  const recipient: PersonName = {
    id: row.id,
    first_name: row.first_name,
    last_name: row.last_name,
  };

  return (
    <tr>
      <td>
        <div className="people-panel__person">
          <Avatar
            src={safePhoto(row.photo_url)}
            fallback={generateInitials(name)}
            alt={generateInitials(name)}
          />
          <div className="min-w-0">
            <Link
              to={profilePath(row)}
              className="people-panel__name"
              title={name || undefined}
            >
              {name || "No name"}
            </Link>
            {detail && (
              <p className="mt-0.5 small-text text-neutral-500 whitespace-nowrap">
                {detail}
              </p>
            )}
          </div>
        </div>
      </td>

      <td>
        <KindChip kind={row.kind} />
      </td>

      <td className="hidden lg:table-cell">
        <Family row={row} orgId={orgId} onChange={onChange} />
      </td>

      {!showOnboarding && (
        <td className="hidden md:table-cell whitespace-nowrap">
          {row.next_meeting ? (
            <NextMeetingChip
              meeting={row.next_meeting}
              onMeetingChange={onChange}
            />
          ) : (
            "–"
          )}
        </td>
      )}

      <td className="people-panel__status">
        <div className="flex flex-wrap gap-1">
          {showOnboarding && row.onboarding ? (
            <OnboardingSteps row={row} orgId={orgId} onChange={onChange} />
          ) : (
            <>
              {row.requests && row.requests.length > 0 && (
                <MeetingRequests
                  requests={row.requests}
                  onAnswered={onChange}
                />
              )}
              {row.tasks && row.tasks.length > 0 && (
                <TaskChips
                  tasks={row.tasks}
                  orgId={orgId}
                  onChange={onChange}
                />
              )}
              <IssueChips row={row} orgId={orgId} onChange={onChange} />
            </>
          )}
        </div>
      </td>

      <td className="people-panel__actions">
        {/* Message, add a task, the person's page, then Dismiss. */}
        <div className="flex items-center justify-end gap-1">
          <Dialog>
            <IconTip label="Message">
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="people-panel__icon"
                  aria-label="Message"
                >
                  <ChatBubbleIcon />
                </button>
              </DialogTrigger>
            </IconTip>

            <ComposeMessageDialog
              students={
                row.kind === PersonKind.STUDENT
                  ? [recipient as StudentLimitedResponse]
                  : undefined
              }
              parents={
                row.kind === PersonKind.PARENT
                  ? [recipient as ParentLimitedResponse]
                  : undefined
              }
              tutors={
                row.kind === PersonKind.TUTOR
                  ? [recipient as TutorLimitedResponse]
                  : undefined
              }
              org_id={orgId}
            />
          </Dialog>

          <QuickAddTask row={row} orgId={orgId} />

          <IconTip label="Open page">
            <Link
              to={profilePath(row)}
              target="_blank"
              className="people-panel__icon"
              aria-label="Open page"
            >
              <ExternalLinkIcon />
            </Link>
          </IconTip>

          {/* Without Dismiss, the same room, so every row's icons line up. */}
          {onDismiss ? (
            <DismissButton onDismiss={onDismiss} />
          ) : (
            <span className="people-panel__icon-slot" aria-hidden />
          )}
        </div>
      </td>
    </tr>
  );
}
